import Cookies from 'js-cookie';
import parsePhoneNumberFromString, { ParseError } from 'libphonenumber-js';
import dynamic from 'next/dynamic';
import { Montserrat } from 'next/font/google';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactNode } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import PhonePasswordLiveModal from '@/components/Live/PhonePasswordLiveModal';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import UpgradeToPremiumModal from '@/components/Subscription/UpgradeToPremiumModal';

import { countryAtom, getGlobalCountry } from '@/atoms/countryState';
import {
  FormDataPassword,
  PasswordModalState,
  PhoneData,
} from '@/pages/live/[slug]';
import { flushAndHydrateGuestEvents } from '@/services/Events/eventBulkProcessor';
import { shouldShowFooter } from '@/utils/ShouldShowFooter';

import ProductsHeader from './ProductsHeader';
import { extractSlugAndProductId } from '../BookLandingPageComp/SlidBookContainer';
import Footer from '../Footer';
import { removeUserId, setUserId } from '../shared/getToken';
import { notify } from '../shared/notify';
import ForgotPasswordModal from '../V2/ForgotPasswordModal';
import LoginModal from '../V2/LoginModal';
import SignUpModal from '../V2/SignUpModal';
import VerifyEmailModal from '../VerifyEmailModal';
import { authModalControl, tokenUpdate } from '../../atoms/authModalControl';
import { chatCount } from '../../atoms/chatCount';
import { localCart } from '../../atoms/LocalCart';
import { localUserID, user } from '../../atoms/user';
import { verifyModal } from '../../atoms/verifyModal';
import appwriteService, { setTokenInCookies } from '../../config/appwrite';
import { appWriteUser, useAuth } from '../../hooks/useAuth';
import usePageTracking from '../../hooks/usePageTracking';
import { affiliateLinkClicks } from '../../services/Affiliate';
import { getCartCount } from '../../services/Cart';
import { getChatCount } from '../../services/Chat';
import {
  liveRegistrationAlreadyRegistered,
  updateUserPhoneNumber,
} from '../../services/Live';
import { getUserProfile } from '../../services/user';
import { footerData } from '../../utils/data';
import useFirebaseNotifications from '../../utils/firebase/firebaseSetup';
import { register } from '../../utils/firebase/serviceWorkerRegistration';
import { AppwriteUserData, User } from '../../utils/interfaces/User';

const CookieConsent = dynamic(
  () => import('@/components/Layout/CookieConsent'),
  { ssr: false },
);

const GoogleOneTap = dynamic(
  () => import('@/components/shared/GoogleOneTap'),
  { ssr: false },
);

interface Props {
  children: ReactNode;
}

export const montserrat = Montserrat({
  subsets: ['latin'],
  fallback: ['Segoe UI', 'Roboto', 'Arial', 'sans-serif'],
  display: 'swap',
});

const Layout = ({ children }: Props) => {
  const router = useRouter();
  const hasEffectRun = useRef(false);
  const [, setLocalCartCount] = useRecoilState(localCart);
  const [, setLocalUserId] = useRecoilState<string>(localUserID);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [userData, setUserData] = useRecoilState<User | undefined>(user);
  const [authModalState, setAuthModalState] = useRecoilState(authModalControl);
  const [visibleVerifyEmailModal, setVisibleVerifyEmailModal] =
    useRecoilState(verifyModal);

  const onCancelCloseSignUpModal = () => {
    setAuthModalState((prevState) => ({
      ...prevState,
      signUp: false,
    }));
  };

  const onCancelCloseLoginModal = () => {
    setAuthModalState((prevState) => ({
      ...prevState,
      login: false,
      emailExistsMessage: false,
    }));
  };

  const onCancelCloseForgotPasswordModal = () => {
    setAuthModalState((prevState) => ({
      ...prevState,
      forgotPassword: false,
    }));
  };

  const [, setChatCount] = useRecoilState<number>(chatCount);
  const { setupNotifications, notification } = useFirebaseNotifications();
  const [notificationsSetup, setNotificationsSetup] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [passwordState, setPasswordState] = useState<PasswordModalState>({
    formData: {
      password: '',
    },
    phoneData: {
      phone: '',
      phoneCountryCode: '',
      phonePrefix: '',
    },
    errors: {
      password: '',
      phone: '',
    },
    modalVisible: false,
    loading: false,
  });

  const { asPath } = router;

  const {
    afc,
    googleLoginCompleted,
    isRegister,
    bookId,
    appleLoginCompleted,
    ref_code,
    courses_slug,
  } = router.query;
  const productId = extractSlugAndProductId(router.asPath);

  const { handlePostSignUpData, onLogOut, identifyUserWithPostHog } = useAuth();
  const globalCountry = getGlobalCountry();
  const countryState = useRecoilValue(countryAtom);

  const isIndia = useMemo(() => {
    const code =
      countryState?.selectedCountry?.code || globalCountry?.countryCode;
    const name =
      countryState?.selectedCountry?.name || globalCountry?.name;

    if (code) {
      return code.toUpperCase() === 'IN';
    }
    if (name) {
      return name.toLowerCase() === 'india';
    }
    return true;
  }, [
    countryState?.selectedCountry?.code,
    countryState?.selectedCountry?.name,
    globalCountry?.countryCode,
    globalCountry?.name,
  ]);

  const isStagingEnv = ['staging', 'production'].includes(
    process.env.NEXT_PUBLIC_APP_ENV as string,
  );

  const solePagePaths = [
    '/auth/forgot-password',
    '/auth/login/',
    '/auth/sign_up',
    '/shop',
    '/shop/bookDetails',
    '/shop/bundleDetails',
    '/privacy_policy/',
    '/error/',
    '/500',
    '/auth/reset-password',
    '/get-our-app',
    '/reader-personality-test',
    '/onboarding',
    '/personality_test',
    '/iframePdfTest',
    '/auth-handler',
    '/pdf/[book_id]/[chapter_id]',
    '/razorpay-checkout/[orderId]',
    '/razorpay-sub-checkout/[orderId]',
    '/corporate/onboarding',
    '/organization-join',
    '/convert-to-prepaid/[hash]',
  ];

  const authPagePaths = [
    '/my-orders/',
    '/manage-addresses/',
    '/profile/',
    '/library/',
    '/broadcast-message/',
    ...(isStagingEnv ? ['/notification/'] : []),
    '/pdf/',
    '/refer-earn/',
    '/rewards/',
    '/rewards/history/',
    '/onboarding/',
    '/reward-store/',
    '/gems-transactions/',
    '/order-successful',
    '/gems-transactions',
  ];
  const links = useMemo(() => {
    const allLinks = [
      { text: 'zAI', route: '/zai', type: 'zai' },
      { text: 'Originals', route: '/books', type: 'books' },
      {
        text: 'Books',
        route: '/oranges',
        type: 'oranges',
      },
      {
        text: 'Bundles',
        route: '/bundles',
        type: 'bundles',
      },
      { text: 'Courses', route: '/courses', type: 'courses' },
      // {
      //   text: 'Schools',
      //   route: '/schools',
      //   type: 'schools',
      // },
      // { text: 'Live', route: '/live', type: undefined, notHaveDropDown: true },
      // { text: 'Partnership', route: '/partnerships', type: 'partnerships' },
      // {
      //   text: 'For Business',
      //   route: 'https://business.zebralearn.com/business',
      // },
      // { text: 'Contact Us', route: '/contact' },
    ];

    if (!isIndia) {
      return allLinks.filter(
        (link) => link.text !== 'Books' && link.type !== 'oranges',
      );
    }

    return allLinks;
  }, [isIndia]);
  useEffect(() => {
    setShowFooter(false);

    const timer = setTimeout(() => {
      setShowFooter(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [router.pathname]);

  const isCorporateOnboarding =
    (router.pathname === '/corporate' || router.pathname === '/corporate/') &&
    !(userData as any)?.organization;

  const isSolePage =
    solePagePaths.includes(router.pathname) ||
    solePagePaths.includes(`${router.pathname}/`) ||
    router.pathname.startsWith('/convert-to-prepaid') ||
    isCorporateOnboarding;

  const isAuthenticated =
    authPagePaths.includes(router.pathname) ||
    authPagePaths.includes(`${router.pathname}/`) ||
    authPagePaths.some((path) => router.pathname.startsWith(path));
  const protectedRoutes = ['/pdf/[book_id]'];

  const asPathProtectedRoutes = ['/pdf/[book_id]/[chapter_id]'];

  const isProtectedRoute = (pathname: string, routes: string[]): boolean => {
    const normalized = pathname.replace(/\/+$/, '');

    return routes.some((route) => {
      const normalizedRoute = route.replace(/\/+$/, '');

      const regex = new RegExp(
        '^' + normalizedRoute.replace(/\[.+?\]/g, '[^/]+') + '$',
      );

      return regex.test(normalized);
    });
  };

  const handlePhoneWithPassword = async () => {
    const { phone, phonePrefix, phoneCountryCode } = passwordState.phoneData;

    if (!phone || !phone.trim()) {
      setPasswordState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          phone: 'Phone number is required',
        },
      }));
      return;
    }

    if (passwordState.errors.phone) {
      return;
    }

    try {
      setPasswordState((prev) => ({
        ...prev,
        loading: true,
      }));

      const prefix = phonePrefix || globalCountry?.phoneCode || '91';
      const countryCode =
        phoneCountryCode ||
        globalCountry?.countryCode?.toLowerCase() ||
        'in';

      await updateUserPhoneNumber(
        { phone, country_code: prefix },
        { ignoreError: true },
      );
      await appwriteService.updateOnboardingPrefs({
        country_code: prefix,
        iso2: countryCode,
      });
      await setUpdatedProfile();

      setPasswordState((prev) => ({
        ...prev,
        modalVisible: false,
        loading: false,
        formData: { password: '' },
        errors: {
          ...prev.errors,
          password: '',
        },
      }));
    } catch (error: any) {
      const message = formateErrorObject(error);

      notify(message, 'error');
      setPasswordState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          password: message,
        },
        loading: false,
      }));
    } finally {
      const { googleSSOSignup, ...rest } = router.query;

      if (googleSSOSignup) {
        router.replace(
          {
            pathname: router.pathname,
            query: rest,
          },
          undefined,
          { shallow: true },
        );
      }
    }
  };

  const handlePhoneInput = ({
    phone,
    phoneCountryCode,
    phonePrefix,
  }: {
    phone: string;
    phoneCountryCode: string;
    phonePrefix: string;
  }) => {
    const cleanedValue = phone.replace(/\D/g, '');

    setPasswordState((prev) => ({
      ...prev,
      phoneData: {
        phone: cleanedValue,
        phoneCountryCode,
        phonePrefix,
      },
    }));

    try {
      if (!cleanedValue.trim()) {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone: 'Phone number is required',
          },
        }));
        return;
      }

      const parsedPhoneNumber = parsePhoneNumberFromString(
        `+${phonePrefix}${cleanedValue}`,
      );

      if (!parsedPhoneNumber) {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone: 'Invalid phone number format',
          },
        }));
      } else if (!parsedPhoneNumber.isValid()) {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone:
              'Invalid phone number. Please check the number and try again.',
          },
        }));
      } else {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone: '',
          },
        }));
      }
    } catch (error: any) {
      if (error instanceof ParseError) {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone: error.message,
          },
        }));
      } else {
        setPasswordState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            phone:
              'An error occurred while validating the phone number. Please try again.',
          },
        }));
      }
    }
  };

  const setFormDataPassword = (
    updater: FormDataPassword | ((prev: FormDataPassword) => FormDataPassword),
  ) => {
    setPasswordState((prev) => ({
      ...prev,
      formData:
        typeof updater === 'function' ? updater(prev.formData) : updater,
    }));
  };

  const setFormDataPhone: React.Dispatch<React.SetStateAction<PhoneData>> = (
    value,
  ) => {
    setPasswordState((prev) => ({
      ...prev,
      phoneData: typeof value === 'function' ? value(prev.phoneData) : value,
    }));
  };

  const checkAuthentication = async () => {
    try {
      const res = await getUserProfile();
      if (res?.data?.zl_uid) {
        flushAndHydrateGuestEvents({
          zl_uid: res.data.zl_uid.toString(),
          userId: res.data?.id?.toString(),
        });
      }
    } catch {
      notify('You are not authenticated', 'error');
      onLogOut();

      let redirectUrl = '/auth/login';

      if (isProtectedRoute(router.pathname, protectedRoutes)) {
        redirectUrl = '/auth/login';
      } else if (isProtectedRoute(router.pathname, asPathProtectedRoutes)) {
        redirectUrl = `/auth/login?redirectUrl=${encodeURIComponent(router.asPath)}`;
      } else {
        redirectUrl = `/auth/login?redirectUrl=${encodeURIComponent(router.pathname)}`;
      }

      router.push(redirectUrl);
    }
  };

  const handleChatCount = async () => {
    try {
      const data = await getChatCount();
      setChatCount(data?.messages);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const setUpdatedProfile = async () => {
    try {
      const data = await getUserProfile();
      setUserData(data?.data);
      setUserId(data?.data?.id as string);
      if (data?.data?.zl_uid) {
        flushAndHydrateGuestEvents({
          zl_uid: data.data.zl_uid.toString(),
          userId: data.data?.id?.toString(),
        });
      }
      if (!data?.data?.phone && router.query.googleSSOSignup) {
        setPasswordState((prev) => ({
          ...prev,
          modalVisible: true,
        }));
      }
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  useEffect(() => {
    const fetchLoggedInStatus = async () => {
      const loggedInStatus = await appwriteService.isLoggedIn();
      setLoggedIn(loggedInStatus);
    };
    fetchLoggedInStatus();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [appwriteService.account, isTokenChanged, router.pathname]);

  useEffect(() => {
    if (loggedIn) {
      setUpdatedProfile();
    }
  }, [loggedIn]);

  useEffect(() => {
    handleCartCount();
  }, [router.pathname]);

  useEffect(() => {
    if (loggedIn) {
      handleChatCount();
    }
  }, [loggedIn, router.pathname]);

  useEffect(() => {
    if (router.isReady && afc && !loggedIn) {
      Cookies.set('afc', afc as string, { expires: 7 });
      handleLinkClicks(afc as string);
    }
    if (router.isReady && ref_code) {
      Cookies.set('ref_code', ref_code as string, { expires: 30 });
    }
  }, [afc, ref_code, router.isReady]);

  useEffect(() => {
    if (router.isReady) {
      const query = router.query;

      if (typeof query === 'object' && query !== null) {
        const filteredQuery: { [key: string]: any } = {};

        for (const key in query) {
          if (Object.prototype.hasOwnProperty.call(query, key)) {
            if (
              typeof key === 'string' &&
              (key.startsWith('mc_') || key.startsWith('utm_'))
            ) {
              filteredQuery[key] = query[key];
            }
          }
        }
        // if (Object.keys(filteredQuery).length > 0) {
        // const filteredQueryStr = JSON.stringify(filteredQuery);
        // const expireTime =
        //   Number(process.env.NEXT_PUBLIC_CASH_UTM_EXPIRY) || 7;
        // Cookies.set('marketing_data', filteredQueryStr, {
        //   expires: expireTime,
        // });
        // }
      }
    }
  }, [router]);

  const handleLinkClicks = async (afc: string) => {
    try {
      await affiliateLinkClicks(afc);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const handleCartCount = async (localId?: string) => {
    try {
      const data = await getCartCount({ localId });
      setLocalCartCount(data.count);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  useEffect(() => {
    if (!router.isReady) return;

    const setUserIdInLocalStorage = async () => {
      const existingUserId = localStorage.getItem('userId');

      if (!existingUserId) {
        const userId = uuidv4();

        localStorage.setItem('userId', userId);
        setTimeout(() => {
          setLocalCartCount(0);
          removeUserId();
        }, 2000);

        setLocalUserId(userId);
      } else {
        setLocalUserId(existingUserId);
      }
    };

    setUserIdInLocalStorage();
  }, [router.asPath, router.isReady]);

  useEffect(() => {
    if (isAuthenticated && !hasEffectRun.current) {
      checkAuthentication();
      hasEffectRun.current = true;
    }
  }, [isAuthenticated, router, checkAuthentication]);

  const handleLiveRegistration = async (
    dbUser: User,
    isRegister: string,
    url: URL,
  ) => {
    try {
      await liveRegistrationAlreadyRegistered({
        email: dbUser?.email as string,
        live_workshop_slug: isRegister,
        name: dbUser?.name as string,
        ecom_user_id: dbUser?.id as number,
        phone: dbUser?.phone as string,
        country_code: dbUser?.country_code as string,
      });
      url.searchParams.delete('isRegister');
    } catch (error) {
      console.error('Live registration failed:', error);
    }
  };

  useEffect(() => {
    const fetchTokenAndTriggerChange = async () => {
      if (googleLoginCompleted === 'true' || appleLoginCompleted === 'true') {
        const token = await setTokenInCookies();

        if (
          router.pathname.includes('/auth/login') ||
          router.pathname.includes('/auth/sign_up')
        ) {
          router.push('/');
        }

        const user = await appwriteService.getCurrentUser();
        const currentUser = user as unknown as AppwriteUserData;
        const dbUser: User = await appWriteUser(currentUser?.userId as string);

        await handlePostSignUpData({
          dbUser,
          token: token as string,
          loginType: appleLoginCompleted ? 'apple' : 'google',
        });

        identifyUserWithPostHog({
          id: dbUser?.zl_uid as string,
          email: dbUser?.email,
          name: dbUser?.name,
        });

        setIsTokenChanged(true);

        if (
          !(
            router.pathname.includes('/auth/login') ||
            router.pathname.includes('/auth/sign_up')
          )
        ) {
          const url = new URL(asPath, window.location.origin);

          if (isRegister) {
            await handleLiveRegistration(dbUser, isRegister as string, url);
          }

          url.searchParams.delete('googleLoginCompleted');
          url.searchParams.delete('appleLoginCompleted');

          router.replace(url.pathname + url.search);
        }
      }
    };

    fetchTokenAndTriggerChange();
  }, [googleLoginCompleted, appleLoginCompleted, isRegister]);

  useEffect(() => {
    if (
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      userData &&
      !notificationsSetup
    ) {
      setupNotifications();
      register();
      notification();
      setNotificationsSetup(true);
    } else if (
      !('Notification' in window) ||
      !('serviceWorker' in navigator) ||
      !('PushManager' in window)
    ) {
      console.error('Notification not supported by your browser');
    }
  }, [userData, notificationsSetup]);

  useEffect(() => {
    if (!userData?.zl_uid || typeof window === 'undefined' || !window.posthog)
      return;

    const identifiedUserId = window.posthog.get_property?.('$user_id');

    if (identifiedUserId !== userData.zl_uid) {
      window.posthog.identify?.(userData.zl_uid, {
        email: userData.email,
        name: userData.name,
      });
    }
  }, [userData?.zl_uid]);

  usePageTracking();

  return (
    <>
      <span suppressHydrationWarning className={montserrat.className}>
        {globalCountry?.name &&
          globalCountry.name !== 'India' &&
          !router.asPath.includes('/cart') &&
          !router.asPath.includes('/checkout') && <CookieConsent />}

        {!isSolePage && (
          <ProductsHeader
            isShadow={router.asPath === '/about_us/'}
            links={links}
          />
        )}

        <ErrorBoundary fallback={<div>Error rendering page.</div>}>
          <div key={router.asPath}>
            {children ?? <div>Page content failed to load</div>}
          </div>
        </ErrorBoundary>

        {shouldShowFooter(router, {
          isSolePage,
          courses_slug: courses_slug as string | null,
          showFooter,
          bookId: bookId as string | null,
          productId,
        }) && <Footer navigation={footerData.navigation} />}

        {globalCountry?.name === 'India' && <UpgradeToPremiumModal />}

        <PhonePasswordLiveModal
          visible={passwordState.modalVisible}
          onCancel={() =>
            setPasswordState((prev) => ({ ...prev, modalVisible: false }))
          }
          errorsPassword={{ password: passwordState.errors.password }}
          errorsPhone={{ phone: passwordState.errors.phone }}
          formDataPassword={passwordState.formData}
          formDataPhone={passwordState.phoneData}
          setFormDataPassword={setFormDataPassword}
          setFormDataPhone={setFormDataPhone}
          handlePhoneInput={handlePhoneInput}
          handlePhoneWithPassword={handlePhoneWithPassword}
          loading={passwordState.loading}
          isGlobal
          isClosable={false}
          userName={userData?.name}
        />

        <GoogleOneTap
          onRequirePhone={() =>
            setPasswordState((prev) => ({
              ...prev,
              modalVisible: true,
            }))
          }
        />

        {isSolePage && (
          <>
            <SignUpModal
              onCancel={onCancelCloseSignUpModal}
              visible={authModalState.signUp}
              noRedirect={authModalState?.noRedirect}
            />
            <LoginModal
              onCancel={onCancelCloseLoginModal}
              visible={authModalState.login}
            />
            <ForgotPasswordModal
              onCancel={onCancelCloseForgotPasswordModal}
              visible={authModalState.forgotPassword}
            />
            <VerifyEmailModal
              visible={visibleVerifyEmailModal}
              onCancel={() => setVisibleVerifyEmailModal(false)}
            />
          </>
        )}
      </span>
    </>
  );
};
export default Layout;
